import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getLoanRolloverSplits,
  addLoanRolloverSplit,
  editLoanRolloverSplit,
  submitLoanRolloverSplit,
  approveLoanRolloverSplit,
  rejectLoanRolloverSplit,
  deleteLoanRolloverSplit,
  stopLoadingLoanRolloverSplit,
  startLoadingLoanRolloverSplit,
} from './actions'
import { LoanRolloverSplitParams } from 'app/models/loan-rollover-split-params'
import { LOAD_FAILED, failed } from 'app/state/utils/load-result'

export const saveLoanRolloverSplit =
  (savedSplit: LoanRolloverSplitParams, existingSplit?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    if (existingSplit) {
      return client
        .put(`${apiServerUrl}/api/draft-loan-rollover-splits`, savedSplit)
        .then(response => {
          const payload = dispatch(editLoanRolloverSplit(response))
          return { success: true, payload: payload.payload }
        })
        .catch(e => {
          return { success: false, payload: e.response?.data?.error }
        })
    }

    return client
      .post(`${apiServerUrl}/api/draft-loan-rollover-splits`, savedSplit)
      .then(response => {
        const payload = dispatch(addLoanRolloverSplit(response))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const loadDraftLoanRolloverSplits = async (
  loanId: string | undefined,
) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(`${apiServerUrl}/api/draft-loan-rollover-splits/loan?id=${loanId}`)
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/draft-loan-rollover-splits`)
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  }
}

export const loadSubmittedLoanRolloverSplits = async (
  loanId: string | undefined,
) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(
        `${apiServerUrl}/api/submitted-loan-rollover-splits/loan?id=${loanId}`,
      )
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/submitted-loan-rollover-splits`)
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  }
}

export const loadApprovedLoanRolloverSplits = async (
  loanId: string | undefined,
) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(
        `${apiServerUrl}/api/approved-loan-rollover-splits/loan?id=${loanId}`,
      )
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/approved-loan-rollover-splits`)
      .then(response => response.data)
      .catch(e => {
        console.log(e)
        return null
      })
  }
}

export const submitForApprovalSplit =
  (split: LoanRolloverSplitParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-loan-rollover-splits/submit`, {
        id: split.id,
      })
      .then(response => {
        const payload = dispatch(submitLoanRolloverSplit(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const approveSelectedLoanRolloverSplit =
  (split: LoanRolloverSplitParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-loan-rollover-splits/approve`, {
        id: split.id,
      })
      .then(response => {
        const payload = dispatch(approveLoanRolloverSplit(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const rejectSelectedLoanRolloverSplit =
  (split: LoanRolloverSplitParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-loan-rollover-splits/reject`, {
        id: split.id,
      })
      .then(response => {
        const payload = dispatch(rejectLoanRolloverSplit(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const deleteSelectedLoanRolloverSplit =
  (split: LoanRolloverSplitParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(
        `${apiServerUrl}/api/draft-loan-rollover-splits?ids[]=${split.id}`,
      )
      .then(() => {
        const payload = dispatch(deleteLoanRolloverSplit(split))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response?.data?.error }
      })
  }

export const loadAllLoanRolloverSplits =
  (loanId?: string | undefined) => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingLoanRolloverSplit())
      let allSplits: any[] = []

      const drafts = await loadDraftLoanRolloverSplits(loanId)
      const submitted = await loadSubmittedLoanRolloverSplits(loanId)
      const approved = await loadApprovedLoanRolloverSplits(loanId)

      if ([drafts, submitted, approved].some(failed)) return LOAD_FAILED

      const submittedUnwrapped = submitted.map(
        (a: { loanRolloverSplit: LoanRolloverSplitParams }) =>
          a.loanRolloverSplit,
      )
      const submittedWithStatus = submittedUnwrapped.map(
        (a: LoanRolloverSplitParams) => ({
          ...a,
          status: 'Submitted',
        }),
      )

      allSplits = drafts
        .map((split: LoanRolloverSplitParams) => ({
          ...split,
          status: 'Draft',
        }))
        .concat(submittedWithStatus)
        .concat(
          approved.map((split: LoanRolloverSplitParams) => ({
            ...split,
            status: 'Approved',
          })),
        )

      return dispatch(getLoanRolloverSplits(allSplits))
    } finally {
      dispatch(stopLoadingLoanRolloverSplit())
    }
  }
